import React, { useState, useRef, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { Container, Icon, Tab, Menu, Label } from "semantic-ui-react";
import { PatronCurrentDocumentRequests } from "./PatronCurrentDocumentRequests";
import { PatronCurrentLoans } from "./PatronCurrentLoans";
import { PatronOverview } from "./PatronOverview";
import { PatronPastDocumentRequests } from "./PatronPastDocumentRequests";
import { PatronPastLoans } from "./PatronPastLoans";
import { PatronPendingLoans } from "./PatronPendingLoans";
import { PatronNotifications } from "./PatronNotificationTab/PatronNotifications";
import Profile from "@authentication/pages/Profile/Profile";
import ProfileDisplay from "@authentication/pages/Profile/ProfileDisplay";
import { isFacultyUser, isLibrarianOrAdmin, getUserTitle } from "UserRoleUtils";
import { authenticationService } from "@authentication/services/AuthenticationService";
import { useLocation } from "react-router-dom";
import { AuthenticationGuard } from "@authentication/components/AuthenticationGuard";
import Overridable from "react-overridable";
import { useIsMobile } from "@utils/mobileUtils";

// Constants
const ICONS = {
  USER: "user circle",
  SETTINGS: "cog",
  TASKS: "tasks",
  BOOK: "book",
  HISTORY: "history",
  BELL: "bell outline",
};

const TAB_KEYS = {
  PROFILE: "profile",
  ACCOUNT_SETTINGS: "account-settings",
  NOTIFICATIONS: "notifications",
  ACTIVITY: "activity",
  CURRENT: "current",
  HISTORY: "history",
};

// Helper Functions
const getShortTabText = (text) => {
  const shortTexts = {
    Profile: "Profile",
    "Account Settings": "Settings",
    Notifications: "Alerts",
    "Your Activity": "Activity",
  };
  return shortTexts[text] || text;
};

const TAB_STYLES = {
  tabPane: { padding: "2rem 0" },
  mainMenu: { secondary: true, pointing: true, size: "huge" },
  activityMenu: { secondary: true, pointing: true, size: "large" },
};

const canViewActivityTab = (user) => {
  if (!user) return false;
  return !authenticationService.hasRoles(user, ["admin", "librarian"]);
};

const canViewNotificationsTab = (user) => {
  if (!user) return false;
  return !authenticationService.hasRoles(user, ["admin"]);
};

// Helper function to extract user types
const extractUserTypes = (userData) => {
  if (Array.isArray(userData.user_types) && userData.user_types.length > 0) {
    return userData.user_types
      .map((userType) => userType.user_type || userType.type || userType.name)
      .filter(Boolean)
      .join(", ");
  }
  return "";
};

// Helper function to process roles with fallback to user_types
const processUserRoles = (userData) => {
  let processedRoles = "";

  if (Array.isArray(userData.roles) && userData.roles.length > 0) {
    processedRoles = userData.roles
      .map((role) =>
        typeof role === "string"
          ? role
          : role.name || role.role || role.type || role.title || String(role)
      )
      .filter(Boolean)
      .join(", ");
  } else if (userData.roles && typeof userData.roles === "string") {
    processedRoles = userData.roles;
  }

  if (!processedRoles) {
    processedRoles = extractUserTypes(userData);
  }

  return processedRoles;
};

// Transform user data to match ReadOnlyProfile expected format
const transformUserDataForProfile = (user) => {
  const profile = {
    email: user.email || "",
    username: user.username || "",
    full_name: user.full_name || user.fullName || user.name || "",
    contact_number:
      user.contact_number || user.contactNumber || user.phone || "",
    department: user.department || "",
    program: user.program || "",
    year: user.year || user.yearLevel || "",
    semester: user.semester || "",
  };

  const userData = {
    id: user.id?.toString() || "",
    roles: processUserRoles(user),
    last_login_at: user.last_login_at || user.lastLoginAt || "",
    location_pid: user.location_pid || user.locationPid || "",
    confirmed_at: user.confirmed_at || user.confirmedAt || "",
    library_id: user.library_id || user.libraryId || "",
    profile_id: user.profile_id || user.profileId || "",
    user_id: user.user_id || user.userId || user.id || "",
    image_url: user.image_url || user.imageUrl || user.avatar || "",
    ...profile,
  };

  return { profile, userData };
};

// Mobile Bottom Navigation Component
const MobileBottomNav = ({ tabs, activeIndex, onTabChange }) => (
  <Menu
    fixed="bottom"
    className="mobile-bottom-navigation"
    style={{
      background: "rgba(255, 255, 255, 0.95)",
      backdropFilter: "blur(10px)",
      borderTop: "1px solid rgba(0, 0, 0, 0.05)",
      boxShadow: "0 -4px 20px rgba(0, 0, 0, 0.1)",
      height: "70px",
      borderRadius: "0",
      margin: "0",
    }}
  >
    {tabs.map((tab, index) => {
      const isActive = activeIndex === index;
      const shortText = getShortTabText(tab.menuItem.content);

      return (
        <Menu.Item
          key={tab.menuItem.key}
          active={isActive}
          onClick={() => onTabChange(null, { activeIndex: index })}
          style={{
            flex: "1",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "8px 4px",
            minHeight: "70px",
            border: "none",
            background: "transparent",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            transform: isActive ? "translateY(-2px)" : "translateY(0)",
          }}
        >
          <div
            style={{
              position: "relative",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <div
              style={{
                padding: "8px",
                borderRadius: "12px",
                background: isActive
                  ? "linear-gradient(135deg, #28a745 0%, #20c997 100%)"
                  : "transparent",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                transform: isActive ? "scale(1.1)" : "scale(1)",
              }}
            >
              <Icon
                name={tab.menuItem.icon}
                size="large"
                style={{
                  margin: "0",
                  color: isActive ? "#ffffff" : "#8e8e93",
                  transition: "color 0.3s ease",
                }}
              />
            </div>
            <Label
              size="mini"
              style={{
                background: "transparent",
                border: "none",
                color: isActive ? "#28a745" : "#8e8e93",
                fontWeight: isActive ? "600" : "500",
                fontSize: "10px",
                margin: "0",
                padding: "0",
                textAlign: "center",
                maxWidth: "60px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                transition: "all 0.3s ease",
              }}
            >
              {shortText}
            </Label>
            {isActive && (
              <div
                style={{
                  width: "4px",
                  height: "4px",
                  borderRadius: "50%",
                  background: "#28a745",
                  marginTop: "2px",
                }}
              />
            )}
          </div>
        </Menu.Item>
      );
    })}
  </Menu>
);

MobileBottomNav.propTypes = {
  tabs: PropTypes.array.isRequired,
  activeIndex: PropTypes.number.isRequired,
  onTabChange: PropTypes.func.isRequired,
};

MobileBottomNav.displayName = "MobileBottomNav";

// Enhanced role detection function with user_types fallback
const getUserRole = (user) => {
  // Option A: Check for specific permission flags (highest priority)
  if (user.isLibrarian || user.is_librarian || user.librarian) {
    return "librarian";
  }

  // Option B: Check user type property with proper null/undefined/type checking
  const userTypeValue = user.type || user.userType || user.user_type;
  if (userTypeValue && typeof userTypeValue === "string") {
    const userType = userTypeValue.toLowerCase();
    return userType; // Return the actual user type (visitor, researcher, etc.)
  }

  // Option C: Check for admin permissions
  if (user.permissions && Array.isArray(user.permissions)) {
    if (user.permissions.includes("librarian")) {
      return "librarian";
    }
  }

  // Option D: Enhanced roles array processing
  if (Array.isArray(user.roles) && user.roles.length > 0) {
    const roles = user.roles.map((role) => {
      const roleName =
        typeof role === "string"
          ? role
          : role.name || role.role || role.type || role.title || String(role);
      return roleName.toLowerCase();
    });

    // Check for librarian/admin roles first (highest priority)
    const librarianRoles = ["librarian", "admin", "administrator"];
    for (const libRole of librarianRoles) {
      if (roles.includes(libRole)) {
        return "librarian";
      }
    }

    // Check for faculty roles
    const facultyRoles = ["faculty", "teacher", "professor", "instructor"];
    for (const facRole of facultyRoles) {
      if (roles.includes(facRole)) {
        return "faculty";
      }
    }

    // Check for visitor roles
    const visitorRoles = ["visitor", "guest"];
    for (const visRole of visitorRoles) {
      if (roles.includes(visRole)) {
        return "visitor";
      }
    }

    // Check for researcher roles
    const researcherRoles = ["researcher", "research", "scientist"];
    for (const resRole of researcherRoles) {
      if (roles.includes(resRole)) {
        return "researcher";
      }
    }

    // Return first role if no specific matches
    return roles[0];
  }

  // Option E: Enhanced user_types array processing (check both user_types and userType)
  const userTypesArray = user.user_types || user.userType;
  if (Array.isArray(userTypesArray) && userTypesArray.length > 0) {
    const userType = userTypesArray[0];
    // Handle different possible property names and ensure case-insensitive comparison
    const extractedType =
      userType.user_type || userType.type || userType.name || userType;

    // Convert to lowercase for comparison, but preserve original case if needed
    const normalizedType =
      typeof extractedType === "string" ? extractedType.toLowerCase() : "";

    // Map specific user types (case-insensitive)
    const typeMapping = {
      faculty: "faculty",
      teacher: "faculty",
      professor: "faculty",
      instructor: "faculty",
      visitor: "visitor",
      guest: "visitor",
      researcher: "researcher",
      research: "researcher",
      scientist: "researcher",
      student: "student",
      librarian: "librarian",
      admin: "librarian",
      administrator: "librarian",
    };

    const mappedType = typeMapping[normalizedType];

    return mappedType || normalizedType || "patron";
  }

  // Option F: Check user_types as a single object
  if (
    user.user_types &&
    typeof user.user_types === "object" &&
    !Array.isArray(user.user_types)
  ) {
    const extractedType = (
      user.user_types.user_type ||
      user.user_types.type ||
      user.user_types.name ||
      ""
    ).toLowerCase();

    if (extractedType) {
      const typeMapping = {
        faculty: "faculty",
        teacher: "faculty",
        professor: "faculty",
        instructor: "faculty",
        visitor: "visitor",
        guest: "visitor",
        researcher: "researcher",
        research: "researcher",
        scientist: "researcher",
        student: "student",
        librarian: "librarian",
        admin: "librarian",
        administrator: "librarian",
      };

      return typeMapping[extractedType] || extractedType;
    }
  }

  return "patron";
};

// Tab Content Components
// Fixed ProfileTabContent component with proper cleanup
const ProfileTabContent = React.memo(({ user, userRole, isMobile }) => {
  const [profileData, setProfileData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [calculatedUserRole, setCalculatedUserRole] = React.useState(userRole);

  useEffect(() => {
    let isMounted = true; // ✅ Track if component is still mounted

    const loadProfileData = async () => {
      try {
        const { authenticationService } = await import(
          "@authentication/services/AuthenticationService"
        );
        const response = await authenticationService.getCurrentUser();
        const userData = response.data || response;

        const extractedProfile = {
          email: userData.email || "",
          username: userData.username || "",
          full_name:
            userData.full_name || userData.fullName || userData.name || "",
          contact_number:
            userData.contact_number ||
            userData.contactNumber ||
            userData.phone ||
            "",
          department: userData.department || "",
          program: userData.program || "",
          year: userData.year || userData.yearLevel || "",
          semester: userData.semester || "",
        };

        const processedUserData = {
          id: userData.id?.toString() || "",
          roles: processUserRoles(userData),
          last_login_at: userData.last_login_at || userData.lastLoginAt || "",
          location_pid: userData.location_pid || userData.locationPid || "",
          confirmed_at: userData.confirmed_at || userData.confirmedAt || "",
          library_id: userData.library_id || userData.libraryId || "",
          profile_id: userData.profile_id || userData.profileId || "",
          user_id: userData.user_id || userData.userId || userData.id || "",
          image_url:
            userData.image_url || userData.imageUrl || userData.avatar || "",
          ...extractedProfile,
        };

        // Recalculate user role from fresh data
        const freshUserRole = getUserRole(userData);

        // ✅ Only update state if component is still mounted
        if (isMounted) {
          setProfileData({
            profile: extractedProfile,
            userData: processedUserData,
          });
          setCalculatedUserRole(freshUserRole);
          setLoading(false);
        }
      } catch (error) {
        const { profile, userData } = transformUserDataForProfile(user);

        // Fallback: use the original user data to calculate role
        const fallbackUserRole = getUserRole(user);

        // ✅ Only update state if component is still mounted
        if (isMounted) {
          setProfileData({ profile, userData });
          setCalculatedUserRole(fallbackUserRole);
          setLoading(false);
        }
      }
    };

    loadProfileData();

    // ✅ Cleanup function to prevent state updates on unmounted component
    return () => {
      isMounted = false;
    };
  }, [user]); // Dependencies array remains the same

  if (loading || !profileData) {
    return (
      <Tab.Pane style={{ padding: isMobile ? "1rem 0.5rem" : "2rem 0" }}>
        <div style={{ textAlign: "center", padding: "2rem" }}>
          <Icon name="spinner" loading size="big" />
          <p>Loading profile...</p>
        </div>
      </Tab.Pane>
    );
  }

  // Use the calculated user role instead of the prop
  const shouldHideAcademicInfo =
    isLibrarianOrAdmin(profileData.userData) ||
    isFacultyUser(calculatedUserRole, user);

  return (
    <Tab.Pane style={{ padding: isMobile ? "1rem 0.5rem" : "2rem 0" }}>
      <ProfileDisplay
        profile={profileData.profile}
        user={profileData.userData}
        needsEmailVerification={!profileData.userData.confirmed_at}
        showAcademicInfo={!shouldHideAcademicInfo}
        title={(() => {
          const title = getUserTitle(calculatedUserRole);
          return title;
        })()}
      />
    </Tab.Pane>
  );
});

ProfileTabContent.displayName = "ProfileTabContent";
ProfileTabContent.propTypes = {
  user: PropTypes.object.isRequired,
  userRole: PropTypes.string.isRequired,
  isMobile: PropTypes.bool.isRequired, // Changed to required since it's used
};

const AccountSettingsTabContent = React.memo(({ isMobile }) => (
  <Tab.Pane style={{ padding: isMobile ? "1rem 0.5rem" : "2rem 0" }}>
    <Profile readOnly={false} />
  </Tab.Pane>
));

AccountSettingsTabContent.displayName = "AccountSettingsTabContent";
AccountSettingsTabContent.propTypes = { isMobile: PropTypes.bool };

const NotificationsTabContent = React.memo(
  ({ patronPid, userRole, isMobile }) => (
    <Tab.Pane style={TAB_STYLES.tabPane} data-tour="notifications-content">
      <PatronNotifications patronPid={patronPid} userRole={userRole} />
    </Tab.Pane>
  )
);

NotificationsTabContent.displayName = "NotificationsTabContent";
NotificationsTabContent.propTypes = {
  patronPid: PropTypes.string.isRequired,
  userRole: PropTypes.string,
  isMobile: PropTypes.bool,
};

const CurrentTabContent = React.memo(
  ({ patronPid, anchors, userRole, user }) => (
    <Tab.Pane>
      <div ref={anchors.currentLoansRef} id="patron-current-loans">
        <PatronCurrentLoans patronPid={patronPid} />
      </div>
      <div ref={anchors.pendingLoansRef} id="patron-pending-loans">
        <PatronPendingLoans patronPid={patronPid} />
      </div>
      {isFacultyUser(userRole, user) && (
        <div ref={anchors.currentDoqRequestsRef} id="patron-current-doqreqs">
          <PatronCurrentDocumentRequests patronPid={patronPid} />
        </div>
      )}
    </Tab.Pane>
  )
);

CurrentTabContent.displayName = "CurrentTabContent";
CurrentTabContent.propTypes = {
  patronPid: PropTypes.string.isRequired,
  anchors: PropTypes.object.isRequired,
  userRole: PropTypes.string.isRequired,
  user: PropTypes.object.isRequired,
};

const HistoryTabContent = React.memo(({ patronPid, userRole, user }) => (
  <Tab.Pane>
    <PatronPastLoans patronPid={patronPid} />
    {isFacultyUser(userRole, user) && (
      <PatronPastDocumentRequests patronPid={patronPid} />
    )}
  </Tab.Pane>
));

HistoryTabContent.displayName = "HistoryTabContent";
HistoryTabContent.propTypes = {
  patronPid: PropTypes.string.isRequired,
  userRole: PropTypes.string.isRequired,
  user: PropTypes.object.isRequired,
};

const ActivityTabContent = React.memo(
  ({ patronPid, anchors, activityTabs, userRole, user, isMobile }) => (
    <Tab.Pane style={{ padding: isMobile ? "1rem 0.5rem" : "2rem 0" }}>
      <PatronOverview
        anchors={anchors}
        userRole={userRole}
        user={user}
        patronPid={patronPid}
      />
      <Tab
        menu={{
          secondary: true,
          pointing: true,
          size: isMobile ? "small" : "large",
        }}
        panes={activityTabs}
        className="patron-activity-subtabs"
      />
    </Tab.Pane>
  )
);

ActivityTabContent.displayName = "ActivityTabContent";
ActivityTabContent.propTypes = {
  patronPid: PropTypes.string.isRequired,
  anchors: PropTypes.object.isRequired,
  activityTabs: PropTypes.array.isRequired,
  userRole: PropTypes.string.isRequired,
  user: PropTypes.object.isRequired,
  isMobile: PropTypes.bool,
};

const PatronProfile = ({ user }) => {
  const currentLoansRef = useRef(null);
  const pendingLoansRef = useRef(null);
  const currentDoqRequestsRef = useRef(null);

  const location = useLocation(); // ✅ detect navigation state
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const isMobile = useIsMobile();

  const anchors = useMemo(
    () => ({
      currentLoansRef,
      pendingLoansRef,
      currentDoqRequestsRef,
    }),
    []
  );

  const userRole = useMemo(() => getUserRole(user), [user]);

  // Activity subtabs
  const activityTabs = useMemo(() => {
    const CurrentTab = () => (
      <CurrentTabContent
        patronPid={user.id}
        anchors={anchors}
        userRole={userRole}
        user={user}
      />
    );
    CurrentTab.displayName = "CurrentTab";

    const HistoryTab = () => (
      <HistoryTabContent patronPid={user.id} userRole={userRole} user={user} />
    );
    HistoryTab.displayName = "HistoryTab";

    return [
      {
        menuItem: {
          key: TAB_KEYS.CURRENT,
          icon: ICONS.BOOK,
          content: "Current",
        },
        render: CurrentTab,
      },
      {
        menuItem: {
          key: TAB_KEYS.HISTORY,
          icon: ICONS.HISTORY,
          content: "History",
        },
        render: HistoryTab,
      },
    ];
  }, [user.id, anchors, userRole, user]);

  // Main tabs
  const mainTabs = useMemo(() => {
    const ProfileTab = () => (
      <ProfileTabContent user={user} userRole={userRole} isMobile={isMobile} />
    );
    ProfileTab.displayName = "ProfileTab";

    const AccountSettingsTab = () => (
      <AccountSettingsTabContent isMobile={isMobile} />
    );
    AccountSettingsTab.displayName = "AccountSettingsTab";

    const NotificationsTab = () => (
      <NotificationsTabContent
        patronPid={user.id}
        userRole={userRole}
        isMobile={isMobile}
      />
    );
    NotificationsTab.displayName = "NotificationsTab";

    const ActivityTab = () => (
      <ActivityTabContent
        patronPid={user.id}
        anchors={anchors}
        activityTabs={activityTabs}
        userRole={userRole}
        user={user}
        isMobile={isMobile}
      />
    );
    ActivityTab.displayName = "ActivityTab";

    const baseTabs = [
      {
        menuItem: {
          key: TAB_KEYS.PROFILE,
          icon: ICONS.USER,
          content: "Profile",
        },
        render: ProfileTab,
      },
      {
        menuItem: {
          key: TAB_KEYS.ACCOUNT_SETTINGS,
          icon: ICONS.SETTINGS,
          content: "Account Settings",
        },
        render: AccountSettingsTab,
      },
    ];

    if (canViewNotificationsTab(user)) {
      baseTabs.push({
        menuItem: {
          key: TAB_KEYS.NOTIFICATIONS,
          icon: ICONS.BELL,
          content: "Notifications",
        },
        render: NotificationsTab,
      });
    }

    if (canViewActivityTab(user)) {
      baseTabs.push({
        menuItem: {
          key: TAB_KEYS.ACTIVITY,
          icon: ICONS.TASKS,
          content: "Your Activity",
        },
        render: ActivityTab,
      });
    }

    return baseTabs;
  }, [user, user.id, userRole, anchors, activityTabs, isMobile]);

  // ✅ Auto-focus "Your Activity" tab when coming from "View your loans"
  useEffect(() => {
    if (location.state?.focusTab) {
      const tabIndex = mainTabs.findIndex(
        (t) => t.menuItem.key === location.state.focusTab
      );

      if (tabIndex !== -1) {
        setActiveTabIndex(tabIndex);
      }
    }
  }, [location.state?.focusTab, mainTabs.length]);

  // ✅ Scroll to Current Loans once tab is open
  useEffect(() => {
    if (
      location.state?.focusTab === "activity" &&
      mainTabs[activeTabIndex]?.menuItem.key === TAB_KEYS.ACTIVITY
    ) {
      setTimeout(() => {
        const element = document.getElementById("patron-current-loans");
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 300);
    }
  }, [location.state, activeTabIndex, mainTabs]);

  const handleTabChange = (e, { activeIndex }) => {
    setActiveTabIndex(activeIndex);
  };

  const AuthorizedComponent = () => (
    <div style={isMobile ? { paddingBottom: "70px", minHeight: "100vh" } : {}}>
      <Container className="spaced">
        {isMobile ? (
          <div className="mobile-layout">
            <div className="mobile-content">
              {mainTabs[activeTabIndex]?.render()}
            </div>
            <MobileBottomNav
              tabs={mainTabs}
              activeIndex={activeTabIndex}
              onTabChange={handleTabChange}
            />
          </div>
        ) : (
          <Tab
            menu={{ secondary: true, pointing: true, size: "huge" }}
            panes={mainTabs}
            className="patron-profile-tab"
            activeIndex={activeTabIndex}
            onTabChange={handleTabChange}
          />
        )}
      </Container>
    </div>
  );

  return <AuthenticationGuard authorizedComponent={AuthorizedComponent} />;
};

PatronProfile.propTypes = {
  user: PropTypes.object.isRequired,
};

PatronProfile.displayName = "PatronProfile";

export default Overridable.component("Patron", PatronProfile);
